/**
 * TaxonomyValueEditor modal component - Override taxonomy values
 * Allows users to manually select or change taxonomy field values
 */

import { Check, X } from 'lucide-react';
import { useState, useEffect } from 'react';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import type { TaxonomyField, TaxonomyValue, TaxonomyOption } from '@/types/taxonomy';

// ============================================================================
// TYPES
// ============================================================================

export interface TaxonomyValueEditorProps {
  field: TaxonomyField;
  currentValue: TaxonomyValue | null;
  allFields: TaxonomyField[];
  allValues: Map<string, TaxonomyValue>;
  isOpen: boolean;
  onClose: () => void;
  onSave: (fieldId: string, optionId: string) => void;
  isSaving?: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get available options for a field based on parent-child relationships
 */
function getAvailableOptions(
  field: TaxonomyField,
  allFields: TaxonomyField[],
  allValues: Map<string, TaxonomyValue>
): TaxonomyOption[] {
  // If no parent field, all options are available
  if (!field.parentFieldId || !field.parentOptionConditions) {
    return field.options;
  }

  // Find parent field and its selected value
  const parentField = allFields.find((f) => f.fieldId === field.parentFieldId);
  if (!parentField) {
    return field.options;
  }

  const parentValue = allValues.get(parentField.fieldId);
  if (!parentValue || parentValue.optionId === 'unknown') {
    // No parent value selected - show all options but maybe disable some
    return field.options;
  }

  // Filter options based on parent option conditions
  const availableOptions = field.options.filter((option) => {
    const validParentOptions = field.parentOptionConditions?.[option.optionId];
    if (!validParentOptions) {
      // No conditions specified - always available
      return true;
    }
    return validParentOptions.includes(parentValue.optionId);
  });

  return availableOptions;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TaxonomyValueEditor({
  field,
  currentValue,
  allFields,
  allValues,
  isOpen,
  onClose,
  onSave,
  isSaving = false,
}: TaxonomyValueEditorProps) {
  const [selectedOptionId, setSelectedOptionId] = useState<string>(
    currentValue?.optionId || ''
  );

  // Reset selection when field changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedOptionId(currentValue?.optionId || '');
    }
  }, [isOpen, currentValue?.optionId]);

  // Get available options based on parent-child relationships
  const availableOptions = getAvailableOptions(field, allFields, allValues);
  const unavailableOptions = field.options.filter(
    (opt) => !availableOptions.find((a) => a.optionId === opt.optionId)
  );

  const handleSave = () => {
    if (selectedOptionId) {
      onSave(field.fieldId, selectedOptionId);
    }
  };

  const handleSelectOption = (optionId: string) => {
    // 'unknown' is a synthetic sentinel — always allow it regardless of availability
    if (optionId === 'unknown') {
      setSelectedOptionId(optionId);
      return;
    }
    // Check if option is available
    const isAvailable = availableOptions.some((opt) => opt.optionId === optionId);
    if (isAvailable) {
      setSelectedOptionId(optionId);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="editor-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md mx-4 bg-surface rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 id="editor-title" className="text-lg font-semibold text-gray-900">
              Edit {field.name}
            </h2>
            {field.guidance && (
              <p className="mt-1 text-sm text-gray-500">{field.guidance}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {/* Current value indicator */}
          {currentValue && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Current value:</p>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">
                  {field.options.find((o) => o.optionId === currentValue.optionId)?.label ||
                    currentValue.optionId}
                </span>
                {currentValue.source === 'user' && (
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                    User confirmed
                  </span>
                )}
                {currentValue.source === 'ai' && (
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                    AI: {currentValue.confidence}%
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Option list */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700 mb-3">Select an option:</p>

            {/* Available options */}
            {availableOptions.map((option) => {
              const isSelected = selectedOptionId === option.optionId;
              return (
                <button
                  key={option.optionId}
                  onClick={() => handleSelectOption(option.optionId)}
                  className={cn(
                    'w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all text-left',
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-900'
                  )}
                >
                  <span className="font-medium">{option.label}</span>
                  {isSelected && <Check className="h-5 w-5 text-blue-600" />}
                </button>
              );
            })}

            {/* Unavailable options (due to parent-child constraints) */}
            {unavailableOptions.length > 0 && (
              <>
                <div className="pt-3 pb-2">
                  <p className="text-xs text-gray-400">
                    Not available for current parent selection:
                  </p>
                </div>
                {unavailableOptions.map((option) => (
                  <div
                    key={option.optionId}
                    className="w-full flex items-center px-4 py-3 rounded-lg border-2 border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed"
                  >
                    <span>{option.label}</span>
                  </div>
                ))}
              </>
            )}

            {/* Unknown option */}
            <button
              onClick={() => handleSelectOption('unknown')}
              className={cn(
                'w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all text-left',
                selectedOptionId === 'unknown'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-dashed border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-500'
              )}
            >
              <span className="italic">Mark as Unknown</span>
              {selectedOptionId === 'unknown' && <Check className="h-5 w-5 text-blue-600" />}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedOptionId || isSaving}
            className="min-w-[80px]"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
